import api from './client';
import { API_ENDPOINTS } from './endpoints';

export interface UserApiItem {
  _id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  alias_name?: string;
  email: string;
  phone_number: string;
  reporting_shift?: string;
  role_name: string;
  employee_designation?: string;
  date_of_joining?: string;
  reporting_manager_id?: string;
  reporting_manager_name?: string;
  reporting_role_name?: string;
  reporting_lead_id?: string;
  reporting_lead_name?: string;
  // FYI-only broadcast list — additional leads notified when QC approves this
  // user's tickets, separate from reporting_lead_id above (the one actual approver).
  reporting_lead_ids?: string[];
  qc_id?: string;
  qc_name?: string;
  reporting_sme_id?: string;
  reporting_sme_name?: string;
  atl_id?: string;
  atl_name?: string;
  senior_manager_id?: string;
  senior_manager_name?: string;
  ceo_id?: string;
  ceo_name?: string;
  dispute_person_id?: string;
  dispute_person_name?: string;
  department_name?: string;
  employee_code?: string;
  daily_target?: number;
  experience_years?: string;
  process?: string[];
  user_code?: string;
  last_working_date?: string;
  deletion_remarks?: string;
  deleted_at?: string;
  meta?: {
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface UsersListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    users?: UserApiItem[];
    totalCount?: number;
    total?: number;
  } | UserApiItem[];
  totalCount?: number;
}

export interface UserMutationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    user?: UserApiItem;
  };
}

export interface CreateUserPayload {
  first_name: string;
  last_name: string;
  alias_name?: string;
  email: string;
  phone_number?: string;
  password: string;
  reporting_shift?: string;
  role_name: string;
  employee_designation?: string;
  date_of_joining?: string;
  experience_years?: string;
  reporting_manager_id?: string;
  reporting_manager_name?: string;
  reporting_role_name?: string;
  reporting_lead_id?: string;
  reporting_lead_name?: string;
  reporting_lead_ids?: string[];
  qc_id?: string;
  qc_name?: string;
  reporting_sme_id?: string;
  reporting_sme_name?: string;
  atl_id?: string;
  atl_name?: string;
  senior_manager_id?: string;
  senior_manager_name?: string;
  ceo_id?: string;
  ceo_name?: string;
  dispute_person_id?: string;
  dispute_person_name?: string;
  department_name?: string;
  employee_code?: string;
  daily_target?: number;
  process?: string[];
}

export interface UpdateUserPayload {
  first_name: string;
  last_name: string;
  alias_name?: string;
  email: string;
  phone_number?: string;
  password?: string;
  reporting_shift?: string;
  role_name: string;
  employee_designation?: string;
  date_of_joining?: string;
  experience_years?: string;
  reporting_manager_id?: string;
  reporting_manager_name?: string;
  reporting_role_name?: string;
  reporting_lead_id?: string;
  reporting_lead_name?: string;
  reporting_lead_ids?: string[];
  qc_id?: string;
  qc_name?: string;
  reporting_sme_id?: string;
  reporting_sme_name?: string;
  atl_id?: string;
  atl_name?: string;
  senior_manager_id?: string;
  senior_manager_name?: string;
  ceo_id?: string;
  ceo_name?: string;
  dispute_person_id?: string;
  dispute_person_name?: string;
  department_name?: string;
  employee_code?: string;
  daily_target?: number;
  process?: string[];
}

export const getUsers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  department_name?: string;
  'meta.status'?: 'active' | 'inactive';
  reporting_manager_id?: string;
  reporting_lead_id?: string;
  qc_id?: string;
  reporting_sme_id?: string;
  atl_id?: string;
  senior_manager_id?: string;
  ceo_id?: string;
  dispute_person_id?: string;
}): Promise<UsersListResponse> => {
  return api.get<UsersListResponse>(API_ENDPOINTS.users.list, { params });
};

export type BulkReassignRelation = 'reporting_manager' | 'reporting_lead' | 'qc' | 'dispute_person';

export interface BulkReassignUsersPayload {
  relation: BulkReassignRelation;
  from_user_id: string;
  to_user_id: string;
  // Omit to move every one of from_user_id's reports under this relation.
  user_ids?: string[];
}

export interface BulkReassignUsersResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    reassigned_count: number;
    repointed_pending_approvals: number;
    results: Array<{ user_id: string; email: string; status: string }>;
  };
}

export const bulkReassignUsers = async (
  payload: BulkReassignUsersPayload,
): Promise<BulkReassignUsersResponse> => {
  return api.post<BulkReassignUsersResponse, BulkReassignUsersPayload>(
    API_ENDPOINTS.users.bulkReassign,
    payload,
  );
};

export const createUser = async (payload: CreateUserPayload): Promise<UserMutationResponse> => {
  return api.post<UserMutationResponse, CreateUserPayload>(API_ENDPOINTS.users.create, payload);
};

export const updateUser = async (id: string, payload: UpdateUserPayload): Promise<UserMutationResponse> => {
  return api.put<UserMutationResponse, UpdateUserPayload>(`${API_ENDPOINTS.users.list}/${id}`, payload);
};

export interface DeleteUserPayload {
  last_working_date: string;
  remarks: string;
}

export const deleteUser = async (id: string, payload: DeleteUserPayload): Promise<UserMutationResponse> => {
  return api.delete<UserMutationResponse, DeleteUserPayload>(`${API_ENDPOINTS.users.list}/${id}`, payload);
};

export const activateUser = async (id: string): Promise<UserMutationResponse> => {
  return api.post<UserMutationResponse, Record<string, never>>(`${API_ENDPOINTS.users.list}/${id}/activate`, {});
};
